import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Plus, Trash2, RotateCcw, Info } from 'lucide-react';

const ACADEMIC_YEARS = ['2025-26', '2026-27', '2027-28'];
const DEFAULT_AY = '2026-27';

/**
 * Teaching Assignments tab — link teacher × class × subject × AY.
 */
export default function AssignmentsTab({ school, subjects, brandColor }) {
  const [assignments, setAssignments] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterYear, setFilterYear] = useState(DEFAULT_AY);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({
    teacher_id: '', school_class_id: '', subject_id: '',
    academic_year: DEFAULT_AY,
  });
  const [saving, setSaving] = useState(false);

  const schoolId = school?.school_id;

  const load = useCallback(async () => {
    if (!schoolId) return;
    setLoading(true);
    try {
      const [a, t, c] = await Promise.all([
        api.get(`/schools/${schoolId}/assignments`),
        api.get(`/schools/${schoolId}/teachers`),
        api.get(`/schools/${schoolId}/classes?status=active`),
      ]);
      setAssignments(a.data || []);
      setTeachers((t.data || []).filter((x) => x.status !== 'inactive'));
      setClasses(c.data || []);
    } catch {
      toast.error('Failed to load assignments');
    } finally {
      setLoading(false);
    }
  }, [schoolId]);

  useEffect(() => { load(); }, [load]);

  const canSubmit = form.teacher_id && form.school_class_id && form.subject_id;

  const submit = async () => {
    if (!canSubmit) {
      toast.error('Teacher, Class, and Subject are all required'); return;
    }
    setSaving(true);
    try {
      await api.post(`/schools/${schoolId}/assignments`, form);
      toast.success('Assignment created');
      setAddOpen(false);
      setForm({
        teacher_id: '', school_class_id: '', subject_id: '',
        academic_year: filterYear,
      });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create assignment');
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (a) => {
    const target = a.status === 'inactive' ? 'active' : 'inactive';
    try {
      await api.patch(`/schools/${schoolId}/assignments/${a.assignment_id}`,
        { status: target });
      toast.success(`Assignment ${target === 'inactive' ? 'removed' : 'restored'}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };

  const filtered = assignments.filter((a) => a.academic_year === filterYear);
  const active = filtered.filter((a) => a.status !== 'inactive');
  const inactive = filtered.filter((a) => a.status === 'inactive');

  const hasPrereqs = teachers.length > 0 && classes.length > 0 && subjects.length > 0;

  return (
    <div data-testid="assignments-tab">
      <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Teaching Assignments</h2>
          <p className="text-sm text-slate-600">
            Link each teacher to the classes and subjects they teach.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filterYear} onValueChange={setFilterYear}>
            <SelectTrigger className="w-[140px]" data-testid="assignments-year-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ACADEMIC_YEARS.map((ay) => (
                <SelectItem key={ay} value={ay}>{ay}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={() => {
              setForm({
                teacher_id: '', school_class_id: '', subject_id: '',
                academic_year: filterYear,
              });
              setAddOpen(true);
            }}
            disabled={!hasPrereqs}
            style={{ backgroundColor: hasPrereqs ? brandColor : undefined }}
            className="text-white disabled:bg-slate-300 disabled:text-slate-500"
            data-testid="add-assignment-btn"
          >
            <Plus size={14} className="mr-1.5" /> New Assignment
          </Button>
        </div>
      </div>

      {!hasPrereqs && (
        <div
          className="border border-blue-200 bg-blue-50 rounded-lg p-4 flex gap-3 mb-4"
          data-testid="assignments-prereq-hint"
        >
          <Info size={18} className="text-blue-600 mt-0.5" />
          <div className="text-sm text-blue-900">
            To create assignments you need at least one <strong>active teacher</strong> and
            one <strong>active class</strong>. Add them in the Teachers and Classes tabs first.
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-16 text-center text-slate-500">Loading…</div>
      ) : active.length === 0 ? (
        <div
          className="border-2 border-dashed rounded-xl py-16 text-center bg-white/60"
          data-testid="assignments-empty"
        >
          <p className="text-slate-600 font-medium">No assignments for {filterYear}</p>
          <p className="text-sm text-slate-500 mt-1">
            Assign teachers to their classes and subjects.
          </p>
        </div>
      ) : (
        <div className="grid gap-2" data-testid="assignments-list">
          {active.map((a) => (
            <div
              key={a.assignment_id}
              className="bg-white border rounded-lg px-4 py-3 flex items-center justify-between"
              data-testid={`assignment-card-${a.assignment_id}`}
            >
              <div className="flex items-center gap-4">
                <div>
                  <div className="font-medium text-slate-900 text-sm">
                    {a.teacher?.name || '—'}
                  </div>
                  <div className="text-xs text-slate-500">{a.teacher?.email}</div>
                </div>
                <span className="text-slate-300">·</span>
                <div>
                  <div className="text-sm font-medium">{a.school_class?.name || '—'}</div>
                  <div className="text-xs text-slate-500">
                    {a.school_class?.academic_year}
                  </div>
                </div>
                <span className="text-slate-300">·</span>
                {a.subject && (
                  <Badge
                    variant="outline"
                    style={{
                      borderColor: a.subject.color || '#CBD5E1',
                      color: a.subject.color || '#475569',
                    }}
                  >
                    {a.subject.name}
                  </Badge>
                )}
              </div>
              <Button
                variant="ghost" size="sm"
                onClick={() => toggleStatus(a)}
                data-testid={`assignment-delete-${a.assignment_id}`}
              >
                <Trash2 size={14} />
              </Button>
            </div>
          ))}
        </div>
      )}

      {inactive.length > 0 && (
        <section className="mt-6" data-testid="inactive-assignments-section">
          <h3 className="text-sm font-semibold text-slate-500 mb-2">
            Removed ({inactive.length})
          </h3>
          <div className="grid gap-2">
            {inactive.map((a) => (
              <div
                key={a.assignment_id}
                className="bg-slate-50 border rounded-lg px-4 py-2 flex items-center justify-between opacity-70 text-sm"
                data-testid={`inactive-assignment-${a.assignment_id}`}
              >
                <span>
                  {a.teacher?.name} · {a.school_class?.name} · {a.subject?.name}
                  <Badge variant="secondary" className="ml-2 text-xs">Inactive</Badge>
                </span>
                <Button
                  variant="ghost" size="sm"
                  onClick={() => toggleStatus(a)}
                  data-testid={`assignment-restore-${a.assignment_id}`}
                >
                  <RotateCcw size={12} className="mr-1" /> Restore
                </Button>
              </div>
            ))}
          </div>
        </section>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-assignment-dialog">
          <DialogHeader>
            <DialogTitle>New Teaching Assignment</DialogTitle>
            <DialogDescription>
              Pick a teacher, a class, and the subject they teach.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Teacher *</Label>
              <Select
                value={form.teacher_id}
                onValueChange={(v) => setForm((f) => ({ ...f, teacher_id: v }))}
              >
                <SelectTrigger data-testid="assign-teacher-select">
                  <SelectValue placeholder="Select teacher" />
                </SelectTrigger>
                <SelectContent>
                  {teachers.map((t) => (
                    <SelectItem key={t.user_id} value={t.user_id}>
                      {t.name} <span className="text-slate-400 ml-1">({t.email})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Class *</Label>
              <Select
                value={form.school_class_id}
                onValueChange={(v) => setForm((f) => ({ ...f, school_class_id: v }))}
              >
                <SelectTrigger data-testid="assign-class-select">
                  <SelectValue placeholder="Select class" />
                </SelectTrigger>
                <SelectContent>
                  {classes
                    .filter((c) => c.academic_year === form.academic_year)
                    .map((c) => (
                      <SelectItem key={c.school_class_id} value={c.school_class_id}>
                        {c.name} · {c.academic_year}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Subject *</Label>
              <Select
                value={form.subject_id}
                onValueChange={(v) => setForm((f) => ({ ...f, subject_id: v }))}
              >
                <SelectTrigger data-testid="assign-subject-select">
                  <SelectValue placeholder="Select subject" />
                </SelectTrigger>
                <SelectContent>
                  {subjects.map((s) => (
                    <SelectItem key={s.subject_id} value={s.subject_id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Academic Year *</Label>
              <Select
                value={form.academic_year}
                onValueChange={(v) =>
                  setForm((f) => ({ ...f, academic_year: v, school_class_id: '' }))
                }
              >
                <SelectTrigger data-testid="assign-year-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ACADEMIC_YEARS.map((ay) => (
                    <SelectItem key={ay} value={ay}>{ay}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline" onClick={() => setAddOpen(false)}
              disabled={saving}
              data-testid="add-assignment-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={submit} disabled={saving || !canSubmit}
              style={{ backgroundColor: brandColor }}
              className="text-white"
              data-testid="add-assignment-submit"
            >
              {saving ? 'Saving…' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
